import { useEffect, useRef, useState } from 'react';
import { apiClient, ApiRequestError } from '../api';
import target from '../config/target.json';
import type { RetailerConfig } from '../modules/Config';
import { PurchaseHistory, transformData } from '../modules/DataTransformSchema';
import { clientLogger } from '../utils/client-logger';
import type { TargetPurchaseRow } from '../lib/connector/index.js';
import {
  type QuestionProTaskStatus,
  useQuestionProTask,
} from './useQuestionProTask';
import type {
  DataConnectionResult,
  PanelEligibility,
} from './useAmazonDataConnection';
import { useInitialParamsSnapshot } from './useInitialParams';
import { hasExternalPanelParam } from '../config/panels';
import {
  createSigninDoneHandler,
  getSigninError,
} from '../lib/signinPollUtils';

type UseTargetDataConnectionProps = {
  onSuccessConnect: (data: PurchaseHistory[], isLoadingMore?: boolean) => void;
  onConnectStart?: () => void;
  onConnectionError?: (errorDetails: string, code?: string) => void;
  onAuthComplete?: (panelEligibility?: PanelEligibility) => void;
  onProgressStep?: (step: number) => void;
  onGoToInitial?: () => void;
};

const retailerConfig = target as RetailerConfig;

const POLL_INTERVAL_MS = 3000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const toPurchaseHistory = (rows: TargetPurchaseRow[]): PurchaseHistory[] =>
  transformData(
    { purchases: rows },
    retailerConfig.dataTransform
  ) as unknown as PurchaseHistory[];

const useTargetDataConnection = ({
  onSuccessConnect,
  onConnectStart,
  onConnectionError,
  onAuthComplete,
  onProgressStep,
  onGoToInitial,
}: UseTargetDataConnectionProps): DataConnectionResult => {
  const { cid, token: tokenFromUrl, tid, player } = useInitialParamsSnapshot();
  const questionProParams = {
    campaignId: cid,
    token: tokenFromUrl,
    tid,
    player,
  };
  const questionProTaskStatus: QuestionProTaskStatus =
    useQuestionProTask(tokenFromUrl);

  // The purchase-history poll loop outlives the render that started it, so it
  // must not close over stale callbacks — handleSuccessConnect reads the live
  // connection state to decide between navigating and merely refreshing orders.
  const onSuccessConnectRef = useRef(onSuccessConnect);
  onSuccessConnectRef.current = onSuccessConnect;
  const onConnectionErrorRef = useRef(onConnectionError);
  onConnectionErrorRef.current = onConnectionError;
  const onProgressStepRef = useRef(onProgressStep);
  onProgressStepRef.current = onProgressStep;

  const [signinId, setSigninId] = useState<string | undefined>(undefined);
  const [credentials, setCredentials] = useState<{
    email: string;
    password: string;
  }>();
  const [isFollowUpForm, setIsFollowUpForm] = useState(false);
  const hasSubmitted = useRef(false);

  const loadPurchaseHistory = async () => {
    onProgressStepRef.current?.(3);
    await apiClient.ensureUser(questionProParams);

    try {
      await apiClient.getTargetPurchaseHistory();
    } catch (error) {
      clientLogger.info('Target purchase history trigger error:', { error });
      const { message, code } = getSigninError(error);
      onConnectionErrorRef.current?.(message, code);
      return;
    }

    onSuccessConnectRef.current([], true);

    let lastSeenId = 0;
    let allRows: TargetPurchaseRow[] = [];

    while (true) {
      try {
        const {
          syncStatus,
          purchases,
          lastSeenId: nextId,
          syncError,
        } = await apiClient.pollTargetPurchaseSync(lastSeenId);

        if (purchases.length > 0) {
          lastSeenId = nextId;
          allRows = [...allRows, ...purchases];
          onSuccessConnectRef.current(toPurchaseHistory(allRows), true);
        }

        if (syncStatus === 'completed') {
          onSuccessConnectRef.current(toPurchaseHistory(allRows), false);
          return;
        }

        if (syncStatus === 'failed') {
          onSuccessConnectRef.current(toPurchaseHistory(allRows), false);
          const msg = syncError ?? 'Target purchase sync failed';
          onConnectionErrorRef.current?.(msg);
          return;
        }
      } catch (error) {
        clientLogger.info('Target purchase sync poll error:', { error });
        const { message, code } = getSigninError(error);
        onConnectionErrorRef.current?.(message, code);
        return;
      }

      await sleep(POLL_INTERVAL_MS);
    }
  };

  const handleSigninDone = createSigninDoneHandler(
    loadPurchaseHistory,
    onAuthComplete,
    (message, code) => onConnectionErrorRef.current?.(message, code),
    'Target'
  );

  const getSigninIdAndPollSigninStatus = async () => {
    try {
      const result = await apiClient.getTargetSignin();

      const signin = 'signin_id' in result ? result.signin_id : undefined;
      if (typeof signin === 'string' && signin) {
        setSigninId(signin);
        return;
      }

      onAuthComplete?.();
      await loadPurchaseHistory();
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 503) {
        onConnectionErrorRef.current?.(
          'All sync slots are currently busy. Please retry shortly.',
          'CAPACITY_FULL'
        );
        return;
      }
      const { message, code } = getSigninError(error);
      clientLogger.info('Target signin error:', { error });
      onConnectionErrorRef.current?.(message, code);
    }
  };

  const handleSubmitPreemptiveForm = ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setCredentials({ email, password });
    onConnectStart?.();
  };

  useEffect(() => {
    if (!credentials?.email || !signinId || hasSubmitted.current) return;
    hasSubmitted.current = true;

    (async () => {
      try {
        const body: Record<string, unknown> = { email: credentials.email };
        if (credentials.password) {
          body.password = credentials.password;
        }

        const response = await apiClient.signin(signinId, body);

        if (response.type === 'NEED_SIGNIN') {
          setIsFollowUpForm(true);
          onGoToInitial?.();
          return;
        }

        if (response.type === 'ERROR') {
          handleSigninDone('ERROR', response.error);
          return;
        }

        handleSigninDone('SUCCESS', undefined, response.panelEligibility);
      } catch (error) {
        const { message, code } = getSigninError(error);
        clientLogger.info('Target submit error:', { error });
        onConnectionError?.(message, code);
      }
    })();
    // oxlint-disable-next-line react/exhaustive-deps
  }, [credentials, signinId]);

  return {
    getSigninIdAndPollSigninStatus,
    signinId,
    selectedRegion: 'US',
    setSelectedRegion: () => {},
    handleSubmitPreemptiveForm,
    handleSigninDone,
    isFollowUpForm,
    questionProTaskStatus,
    hasExternalPanel: hasExternalPanelParam({ tid }),
    hasPrefetchedCredentials: false,
    shouldRedirectToExternal: true,
  };
};

export default useTargetDataConnection;
